import { memo, useMemo } from 'react';

import Table, { ColumnOption, renderDate } from 'domains/core/components/Table';
import TruncatedTextWithTooltip from 'domains/core/components/TruncatedTextWithTooltip';
import { DisplayConditionListItem } from 'hooks/mutations/useArchiveDisplayCondition';

import ManageDisplayConditionsTableOptionsCell from './ManageDisplayConditionsTableOptionsCell';

export type Props = {
    conditions: DisplayConditionListItem[];
    isLoading?: boolean;
    isRefetching?: boolean;
    isArchivedView: boolean;
    onEditCondition: (condition: DisplayConditionListItem) => void;
    /** When provided, `Table` renders a refresh button in the header that calls this. */
    refetch?: () => void;
};

const ManageDisplayConditionsTable = ({
    conditions,
    isLoading,
    isRefetching,
    isArchivedView,
    onEditCondition,
    refetch,
}: Props) => {
    const data: object[] = useMemo(
        () =>
            conditions.map((condition) => ({
                ...condition,
                dateCreated: new Date(condition.createdTimestamp),
                dateUpdated: new Date(condition.lastModifiedTimestamp),
            })),
        [conditions]
    );

    const columns: ColumnOption[] = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'label',
                align: 'left',
                disableSortBy: true,
                sx: { width: '35%' },
                Cell: (val: any) => (
                    <TruncatedTextWithTooltip
                        value={val.cell.value ?? ''}
                        maxWidth="100%"
                        dataTestId={`manage-display-condition-name-${val.row.original.id}`}
                    />
                ),
            },
            {
                Header: 'Category',
                accessor: 'category',
                align: 'left',
                disableSortBy: true,
                sx: { width: '20%' },
                Cell: (val: any) => (
                    <TruncatedTextWithTooltip
                        value={val.cell.value ?? ''}
                        maxWidth="100%"
                        dataTestId={`manage-display-condition-category-${val.row.original.id}`}
                    />
                ),
            },
            {
                Header: 'Date Created',
                accessor: 'dateCreated',
                Cell: renderDate,
                disableSortBy: true,
                sx: { width: '17%' },
            },
            {
                Header: 'Date Updated',
                accessor: 'dateUpdated',
                Cell: renderDate,
                disableSortBy: true,
                sx: { width: '17%' },
            },
            {
                Header: '',
                accessor: 'id',
                align: 'right',
                disableSortBy: true,
                sx: { width: '56px' },
                Cell: (val: any) => (
                    <ManageDisplayConditionsTableOptionsCell
                        condition={val.row.original as DisplayConditionListItem}
                        isArchivedView={isArchivedView}
                        onEdit={onEditCondition}
                    />
                ),
            },
        ],
        [isArchivedView, onEditCondition]
    );

    return <Table data={data} columns={columns} isLoading={isLoading} isRefetching={isRefetching} refetch={refetch} />;
};

export default memo(ManageDisplayConditionsTable);
